import {
  createContext,
  CSSProperties,
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MapContext } from '@react-google-maps/api';
import { useSharedMapEvents } from '@bridebook/web/components/gmap/shared-map/use-shared-map-events';

const MAP_ID = 'b51a366f5af51268';

interface SharedMapContextValue {
  map: google.maps.Map | null;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  attachToContainer: (container: HTMLDivElement) => void;
}

const SharedMapContext = createContext<SharedMapContextValue | null>(null);

/**
 * Context provider that owns a single Google Maps `Map` instance and makes it reusable
 * across different parts of the React tree.
 *
 * Key behavior:
 * - Creates the map once (lazy) on the first container attachment.
 * - Reuses the same map instance on later attachments by moving the map DIV into the new container.
 * - Preserves viewport state (center and zoom) when reattaching.
 *
 * Usage:
 * ```tsx
 * <SharedMapProvider>
 *   <SharedMap ... />
 * </SharedMapProvider>
 * ```
 */
export const SharedMapProvider = ({ children }: PropsWithChildren) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const attachToContainer = useCallback((container: HTMLDivElement) => {
    if (typeof google === 'undefined') return;

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(container, { mapId: MAP_ID });
      setMap(mapRef.current);
    } else {
      const currentMap = mapRef.current;
      const center = currentMap.getCenter()?.toJSON();
      const zoom = currentMap.getZoom();

      container.appendChild(currentMap.getDiv());
      google.maps.event.trigger(currentMap, 'resize');

      if (center) currentMap.setCenter(center);
      if (zoom !== undefined) currentMap.setZoom(zoom);
    }
  }, []);

  return (
    <SharedMapContext.Provider value={{ map, mapRef, attachToContainer }}>
      {children}
    </SharedMapContext.Provider>
  );
};

interface SharedMapContainerProps extends PropsWithChildren {
  className?: string;
  style?: CSSProperties;
}

const SharedMapContainerComp = ({ className, style, children }: SharedMapContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isAttached = useRef(false);
  const { attachToContainer } = useSharedMapContext();

  useEffect(() => {
    const container = containerRef.current;
    if (!container || isAttached.current) return;

    isAttached.current = true;
    attachToContainer(container);
  }, [attachToContainer]);

  return (
    <div id="shared-map-container" ref={containerRef} className={className} style={style}>
      {children}
    </div>
  );
};

/**
 * A lightweight container component that hosts the shared Google Map DOM node.
 *
 * Responsibilities:
 * - Creates a container DIV and calls `attachToContainer` once on mount.
 * - Prevents double-attachment using an internal ref guard.
 * - Renders children on top of the map container (e.g., overlays, custom controls).
 *
 * Typical usage:
 * - Used internally by `SharedMap`, but can be used directly if you need fine-grained control.
 */
export const SharedMapContainer = memo(SharedMapContainerComp);

export const useSharedMapContext = () => {
  const context = useContext(SharedMapContext);
  if (!context) {
    throw new Error('useSharedMapContext must be used within a SharedMapProvider');
  }
  return context;
};

type SharedMapOptionKeys =
  | 'cameraControl'
  | 'clickableIcons'
  | 'disableDefaultUI'
  | 'disableDoubleClickZoom'
  | 'draggable'
  | 'fullscreenControl'
  | 'gestureHandling'
  | 'isFractionalZoomEnabled'
  | 'keyboardShortcuts'
  | 'mapTypeControl'
  | 'maxZoom'
  | 'minZoom'
  | 'scrollwheel'
  | 'streetViewControl'
  | 'zoomControl'
  | 'zoomControlOptions';

/**
 * IMPORTANT:
 * SharedMap merges `options` (instead of replacing them).
 * If an option isn’t explicitly set, it may carry over from a previous map instance because options are merged.
 *
 * Therefore every option in `SharedMapStrictOptions` is **required** so each SharedMap usage
 * explicitly overwrites the full supported options set.
 *
 * If you need another map option:
 * 1) Add the key to `SharedMapOptionKeys`
 * 2) Update every SharedMap usage to set it explicitly
 */
export type SharedMapStrictOptions = Required<Pick<google.maps.MapOptions, SharedMapOptionKeys>>;

interface SharedMapProps extends PropsWithChildren {
  mapContainerClassName?: string;
  mapContainerStyle?: CSSProperties;
  options: SharedMapStrictOptions;
  onClick?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onZoomChanged?: () => void;
  onMouseUp?: () => void;
}

const SharedMapComp = ({
  children,
  mapContainerClassName,
  mapContainerStyle,
  options,
  onClick,
  onDragStart,
  onDragEnd,
  onZoomChanged,
  onMouseUp,
}: SharedMapProps) => {
  const { map } = useSharedMapContext();

  useSharedMapEvents({
    onClick,
    onDragStart,
    onDragEnd,
    onZoomChanged,
    onMouseUp,
  });

  useEffect(() => {
    if (map && options) {
      map.setOptions(options);
    }
  }, [map, options]);

  return (
    <SharedMapContainer className={mapContainerClassName} style={mapContainerStyle}>
      <MapContext.Provider value={map}>{children}</MapContext.Provider>
    </SharedMapContainer>
  );
};

/**
 * High-level component that:
 * - Ensures the shared map is attached to a DOM container.
 * - Bridges the shared `google.maps.Map` instance into `@react-google-maps/api` via `MapContext.Provider`.
 * - Wires shared map events through `useSharedMapEvents`.
 * - Applies map options when provided.
 *
 * Notes:
 * - `options` are applied whenever `map` becomes available or `options` changes.
 * - Event callbacks are registered via `useSharedMapEvents` (shared event layer).
 *
 * Usage:
 * ```tsx
 * <SharedMapProvider>
 *   <SharedMap
 *     mapContainerStyle={{ height: 400, width: '100%' }}
 *     options={{ minZoom: 4, maxZoom: 17 }}
 *     onClick={() => console.log('map click')}
 *   >
 *     { ...children that rely on MapContext - usually map markers, polylines, etc... }
 *   </SharedMap>
 * </SharedMapProvider>
 * ```
 */
export const SharedMap = memo(SharedMapComp);
